import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";

export const youtubeId = (url = "") => {
  const m = url.match(/(?:youtube\.com\/(?:shorts\/|embed\/|watch\?(?:.*&)?v=)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : "";
};

let apiPromise;
const loadApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    });
  }
  return apiPromise;
};

export default function TourVideo({ url, title }) {
  const id = youtubeId(url);
  const host = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id || !host.current) return undefined;
    let player;
    loadApi().then((YT) => {
      if (!host.current) return;
      player = new YT.Player(host.current, {
        videoId: id, width: "100%", height: "100%",
        playerVars: { autoplay: 1, mute: 1, loop: 1, playlist: id, playsinline: 1, controls: 1, rel: 0, modestbranding: 1 },
        events: { onReady: (e) => { e.target.mute(); e.target.playVideo(); }, onError: () => setFailed(true) },
      });
    });
    return () => { try { player?.destroy(); } catch { /* noop */ } };
  }, [id]);

  if (!id) return null;
  return (
    <div className="w-full max-w-[280px] sm:max-w-[300px] mx-auto lg:mx-0 shrink-0" data-testid="tour-video">
      <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-2xl shadow-black/40 ring-1 ring-white/25">
        {failed ? (
          <a href={url} target="_blank" rel="noreferrer" className="group absolute inset-0 block" data-testid="tour-video-fallback">
            <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt={`${title} video`} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <span className="absolute inset-0 grid place-items-center">
              <span className="flex items-center gap-2 bg-terra text-white font-semibold text-sm px-5 py-3 rounded-full shadow-lg"><Play size={16} className="fill-white" /> Watch on YouTube</span>
            </span>
          </a>
        ) : (
          <div className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full"><div ref={host} data-testid="tour-video-player" /></div>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[11px] font-mono uppercase tracking-widest text-white/70">
        <span data-testid="tour-video-caption">{failed ? "Advertisement video" : "Tap the speaker for sound"}</span>
        <a href={url} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-white" data-testid="tour-video-youtube-link">Open on YouTube</a>
      </div>
    </div>
  );
}
